import { ref, computed, readonly } from "vue";

interface Config {
  ApiBaseUrl: string;
  SignalRUrl: string;
  OidcAuthority: string | undefined;
  OidcDomain: string | undefined;
  OidcClientId: string | undefined;
  SentryDsn: string | undefined;
  SentryEnvironment: string;
  SentrySendDefaultPii: boolean | string;
  SentryTracesSampleRate: number;
  SentryProfilesSampleRate: number;
  SentryReplaySessionSampleRate: number;
  SentryReplayOnErrorSampleRate: number;
  SentryTracePropagationTargets: string[];
  OidcUserIdClaimName: string;
}

function getRuntimeConfig(): Partial<Config> {
  if (
    window.hasOwnProperty("_env_") &&
    typeof (window as any)["_env_"] === "object"
  ) {
    return (window as any)._env_ || {};
  }
  return {};
}

// Create a reactive reference for runtime config updates
const runtimeConfig = ref<Partial<Config>>(getRuntimeConfig());

export function useConfig() {
  // Computed config that merges environment variables with runtime config
  const config = computed<Config>(() => ({
    ApiBaseUrl:
      runtimeConfig.value.ApiBaseUrl ||
      import.meta.env.VITE_API_BASE_URL ||
      "https://localhost:7234/api/v1",
    SignalRUrl:
      runtimeConfig.value.SignalRUrl ||
      import.meta.env.VITE_SIGNALR_URL ||
      "https://localhost:7234/hubs/frontends",
    OidcAuthority:
      runtimeConfig.value.OidcAuthority || import.meta.env.VITE_OIDC_AUTHORITY,
    OidcDomain:
      runtimeConfig.value.OidcDomain || import.meta.env.VITE_OIDC_DOMAIN,
    OidcClientId:
      runtimeConfig.value.OidcClientId || import.meta.env.VITE_OIDC_CLIENT_ID,
    OidcUserIdClaimName:
      runtimeConfig.value.OidcUserIdClaimName ||
      import.meta.env.VITE_OIDC_USER_ID_CLAIM_NAME ||
      "http://localhost:5173/user_id",
    SentryDsn: runtimeConfig.value.SentryDsn || import.meta.env.VITE_SENTRY_DSN,
    SentryEnvironment:
      runtimeConfig.value.SentryEnvironment ||
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      "development",
    SentrySendDefaultPii:
      runtimeConfig.value.SentrySendDefaultPii ||
      import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII ||
      false,
    SentryTracesSampleRate:
      runtimeConfig.value.SentryTracesSampleRate ||
      parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) ||
      0.1,
    SentryProfilesSampleRate:
      runtimeConfig.value.SentryProfilesSampleRate ||
      parseFloat(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE) ||
      0.1,
    SentryReplaySessionSampleRate:
      runtimeConfig.value.SentryReplaySessionSampleRate ||
      parseFloat(import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE) ||
      0.05,
    SentryReplayOnErrorSampleRate:
      runtimeConfig.value.SentryReplayOnErrorSampleRate ||
      parseFloat(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE) ||
      1.0,
    SentryTracePropagationTargets:
      runtimeConfig.value.SentryTracePropagationTargets ||
      (import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS
        ? import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS.split(",")
        : ["localhost"]),
  }));

  return {
    config: readonly(config),
  };
}
